import { useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { BoardColumnPage } from '@/types/api';
import { useBoardView } from './useBoardView';

/**
 * How many of your cards have something you have not read.
 *
 * DERIVED from the board cache, not fetched. The count already comes back on every
 * column response, so asking the server again for a number it just sent would be a
 * request that can only ever agree with data we hold.
 *
 * Subscribed to the query cache rather than read once: the board refetches constantly
 * — realtime, pagination, filters — and a header badge that only updated when the
 * shell happened to re-render would drift within seconds.
 *
 * Returns null when the board has not loaded. On the People screen there is nothing to
 * count, and showing 0 would claim you have no unread work rather than admitting we
 * have not looked.
 */
export function useUnreadCards(): number | null {
  const queryClient = useQueryClient();
  /*
    Scoped, because an admin who has used the switch has BOTH boards in the cache for
    as long as the unused one survives garbage collection. Counting every entry would
    add the two together and report double.
  */
  const { scope } = useBoardView();

  return useSyncExternalStore(
    (onChange) => queryClient.getQueryCache().subscribe(onChange),
    () => {
      const entries = queryClient.getQueriesData<{ pages: BoardColumnPage[] }>({
        queryKey: ['boardColumn', scope],
      });
      if (entries.length === 0) return null;

      let total = 0;
      let seen = false;
      for (const [, data] of entries) {
        // Every page carries the column-wide count, so the last one is as good as any
        // — and is the freshest.
        const last = data?.pages.at(-1);
        if (!last) continue;
        seen = true;
        total += last.unread;
      }
      return seen ? total : null;
    },
    () => null,
  );
}
